import { isKnownErrorResponse, type ErrorResponse } from '$shared/lib';

type ErrorData = {
  url?: string;
  status?: number;
  response?: ErrorResponse | Response | unknown;
  data?: ErrorResponse;
  isAbort?: boolean;
  originalError?: unknown;
};

/**
 * ClientResponseError is a custom Error class that is intended to wrap
 * and normalize any error thrown by `Client.send()`.
 */
export class ClientResponseError extends Error {
  url: string;
  status: number;
  response: ErrorResponse | Response | unknown;
  originalError: unknown;

  constructor(errData: ErrorData = {}) {
    super();

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, ClientResponseError.prototype);

    this.url = errData.url ?? '';
    this.status = errData.status ?? 0;
    this.originalError = errData.originalError ?? null;
    this.name = `ClientResponseError ${this.status}`;
    this.response = errData.response;

    // Handle the case where response is a APIErrorResponse object
    if (isKnownErrorResponse(errData.response)) {
      // The message is only set for known api errors
      this.message = errData.response.message;
    } else if (errData.response instanceof Response) {
      this.message = 'Oops! Something went wrong';
    } else {
      this.message = 'An unknown error occurred';
    }

    if (!this.originalError && !(errData instanceof ClientResponseError)) {
      this.originalError = errData;
    }
  }

  /**
   * Alias for `this.response` to preserve backward compatibility.
   */
  get data() {
    return this.response;
  }

  /**
   * Make a POJO copy of the current error class instance.
   */
  toJSON(): Record<string, unknown> {
    return {
      url: this.url,
      status: this.status,
      response: this.response,
      name: this.name,
      message: this.message,
      data: this.data,
    };
  }
}
